import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth0Safe as useAuth0, PRIJAVA_INTENT_KEY } from '@/hooks/useAuth0Safe';
import { LogIn, UserPlus, ArrowLeft, ShieldCheck, Bookmark, Bell, Heart, Sparkles, Crown, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIJAVA_INTERESTS_KEY = 'emina_prijava_interests_pending_v1';

const PRIJAVA_INTERESTS = [
  { key: 'lokalne-vijesti', label: 'Lokalne vijesti' },
  { key: 'sport', label: 'Sport' },
  { key: 'kultura', label: 'Kultura' },
  { key: 'gospodarstvo', label: 'Gospodarstvo' },
  { key: 'politika', label: 'Politika' },
  { key: 'obrazovanje', label: 'Obrazovanje' },
  { key: 'zdravstvo', label: 'Zdravstvo' },
  { key: 'crna-kronika', label: 'Crna kronika' },
  { key: 'turizam', label: 'Turizam' },
  { key: 'gastronomija', label: 'Gastronomija' },
  { key: 'medjimurje', label: 'Međimurje' },
  { key: 'varazdin', label: 'Varaždin' },
  { key: 'svijet', label: 'Svijet' },
  { key: 'kripto', label: 'Kripto' },
];

/**
 * Branded e-mina prijava (login) wrap shown to public visitors.
 *
 * Hits Auth0 with `screen_hint` so users can pick whether to sign in OR
 * register, and also offers one-click social logins (Google, Facebook).
 * The route is `/prijava` and is wired from `useAuth0Safe` so EVERY
 * `loginWithRedirect()` call across the platform (chat, comments,
 * paywall, bookmark…) lands on this branded screen first.
 */
export default function PrijavaPage() {
  const navigate = useNavigate();
  const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0();
  const [busy, setBusy] = useState(null); // 'login' | 'signup' | 'google' | 'facebook'
  const [branding, setBranding] = useState({ header_logo_url: '/logo.png' });
  const [promo, setPromo] = useState(null);
  const [picked, setPicked] = useState(() => {
    try {
      const saved = localStorage.getItem(PRIJAVA_INTERESTS_KEY);
      return new Set(saved ? JSON.parse(saved) : []);
    } catch { return new Set(); }
  });

  function toggleInterest(k) {
    setPicked(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      try { localStorage.setItem(PRIJAVA_INTERESTS_KEY, JSON.stringify([...next])); } catch {}
      return next;
    });
  }

  useEffect(() => {
    fetch(`${API}/public/homepage-settings`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.branding) setBranding(prev => ({ ...prev, ...d.branding })); })
      .catch(() => {});
    fetch(`${API}/public/promo-banner`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.active) setPromo(d); })
      .catch(() => {});
  }, []);

  // If already logged in — bounce back to homepage
  useEffect(() => {
    if (!isLoading && isAuthenticated) navigate('/', { replace: true });
  }, [isLoading, isAuthenticated, navigate]);

  function go(opts, key) {
    setBusy(key);
    // Merge with any saved intent from a previous loginWithRedirect call
    let saved = null;
    try { saved = JSON.parse(sessionStorage.getItem(PRIJAVA_INTENT_KEY) || 'null'); } catch {}
    sessionStorage.removeItem(PRIJAVA_INTENT_KEY);
    const merged = {
      ...(saved || {}),
      ...(opts || {}),
      auth0Direct: true,
      authorizationParams: {
        ...(saved?.authorizationParams || {}),
        ...(opts?.authorizationParams || {}),
      },
    };
    try { loginWithRedirect(merged); } catch { setBusy(null); }
  }

  const logoSrc = branding.header_logo_url?.startsWith('/api/')
    ? `${process.env.REACT_APP_BACKEND_URL}${branding.header_logo_url}`
    : (branding.header_logo_url || '/logo.png');

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-10
                 bg-[radial-gradient(ellipse_at_top,_#1e1b4b_0%,_#0b1220_45%,_#020617_100%)]
                 text-white relative overflow-hidden"
      data-testid="prijava-page"
    >
      <div className="pointer-events-none absolute inset-0 opacity-[0.06]"
           style={{ backgroundImage: "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22120%22 height=%22120%22><filter id=%22n%22><feTurbulence baseFrequency=%220.9%22/></filter><rect width=%22100%22 height=%22100%22 filter=%22url(%23n)%22/></svg>')" }} />

      <button type="button" onClick={() => navigate('/')}
        className="absolute top-5 left-5 flex items-center gap-1.5 text-xs text-slate-300 hover:text-white"
        data-testid="prijava-back-home">
        <ArrowLeft size={14} /> Natrag na portal
      </button>

      <div className="relative w-full max-w-md">
        <div className="flex justify-center mb-6">
          <img src={logoSrc} alt="e-mina.eu" className="h-12 drop-shadow-lg" />
        </div>

        {/* Promo banner — first 100 registered get 1 month free premium */}
        {promo && (
          <div className="mb-4 rounded-2xl px-4 py-3 bg-gradient-to-r from-amber-500/95 via-rose-500/95 to-fuchsia-600/95
                          text-white shadow-lg border border-amber-300/40 backdrop-blur-md
                          animate-[pulse_3s_ease-in-out_infinite]"
               data-testid="prijava-promo-banner">
            <div className="flex items-start gap-3">
              <Crown size={22} className="shrink-0 mt-0.5 drop-shadow" />
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-extrabold tracking-tight leading-tight">
                  {promo.title || 'Prvih 100 registriranih dobiva 1 mjesec premium besplatno!'}
                </p>
                {promo.subtitle && (
                  <p className="text-[11px] text-white/90 mt-0.5">{promo.subtitle}</p>
                )}
                <div className="flex items-center gap-3 mt-1.5 text-[10px] font-bold uppercase tracking-widest">
                  {typeof promo.remaining_slots === 'number' && (
                    <span className="bg-white/25 rounded-full px-2 py-0.5"
                          data-testid="prijava-promo-remaining">
                      Preostalo: {promo.remaining_slots} mjesta
                    </span>
                  )}
                  {promo.ends_in_days != null && (
                    <span className="bg-black/30 rounded-full px-2 py-0.5">
                      Do isteka: {promo.ends_in_days} dana
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl p-7"
             style={{ boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5), 0 0 0 1px rgba(220,38,38,0.08) inset' }}>
          <h1 className="text-2xl font-bold text-center tracking-tight" data-testid="prijava-title">
            Dobrodošli na <span className="text-red-500">e-mina</span>
          </h1>
          <p className="text-center text-sm text-slate-400 mt-1.5 mb-6">
            Prijavite se ili otvorite besplatan račun u 30 sekundi.
          </p>

          <button type="button"
            onClick={() => go({ authorizationParams: { screen_hint: 'signup' } }, 'signup')}
            disabled={!!busy}
            className="w-full py-3 px-4 rounded-xl bg-red-600 hover:bg-red-700 active:scale-[0.99]
                       transition-all duration-150 text-sm font-bold flex items-center justify-center gap-2
                       shadow-lg shadow-red-900/40 disabled:opacity-60"
            data-testid="prijava-signup-btn">
            <UserPlus size={16} />
            {busy === 'signup' ? 'Otvaram registraciju…' : 'Otvori besplatan račun'}
          </button>

          <p className="text-center text-[11px] text-slate-500 mt-2">
            Bez kreditne kartice. Otkažite kad god želite.
          </p>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-slate-700/60" />
            <span className="text-[10px] uppercase tracking-widest text-slate-500">ili</span>
            <div className="flex-1 h-px bg-slate-700/60" />
          </div>

          <div className="space-y-2.5">
            <button type="button"
              onClick={() => go({ authorizationParams: { connection: 'google-oauth2' } }, 'google')}
              disabled={!!busy}
              className="w-full py-2.5 px-4 rounded-xl bg-white text-slate-900 hover:bg-slate-100
                         transition-colors text-sm font-semibold flex items-center justify-center gap-2.5
                         disabled:opacity-60"
              data-testid="prijava-google-btn">
              <GoogleIcon /> Nastavi s Google računom
            </button>
            <button type="button"
              onClick={() => go({ authorizationParams: { connection: 'facebook' } }, 'facebook')}
              disabled={!!busy}
              className="w-full py-2.5 px-4 rounded-xl bg-[#1877F2] hover:bg-[#0e63d6]
                         transition-colors text-sm font-semibold flex items-center justify-center gap-2.5
                         disabled:opacity-60"
              data-testid="prijava-facebook-btn">
              <FacebookIcon /> Nastavi s Facebook računom
            </button>
          </div>

          <div className="mt-6 pt-5 border-t border-slate-800">
            <p className="text-center text-xs text-slate-400 mb-3">Već imate račun?</p>
            <button type="button"
              onClick={() => go({ authorizationParams: { screen_hint: 'login' } }, 'login')}
              disabled={!!busy}
              className="w-full py-2.5 px-4 rounded-xl border border-slate-700 hover:border-red-500/60
                         hover:bg-slate-800/60 transition-colors text-sm font-medium flex items-center
                         justify-center gap-2 text-slate-200 hover:text-white disabled:opacity-60"
              data-testid="prijava-login-btn">
              <LogIn size={15} />
              {busy === 'login' ? 'Otvaram prijavu…' : 'Prijavite se e-mail adresom'}
            </button>
          </div>

          <ul className="mt-6 space-y-2 text-[12px] text-slate-300">
            <li className="flex items-start gap-2">
              <Bookmark size={14} className="text-amber-400 mt-0.5 shrink-0" />
              Spremanje članaka za kasnije čitanje
            </li>
            <li className="flex items-start gap-2">
              <Bell size={14} className="text-amber-400 mt-0.5 shrink-0" />
              Personalizirane obavijesti o važnim vijestima
            </li>
            <li className="flex items-start gap-2">
              <Heart size={14} className="text-amber-400 mt-0.5 shrink-0" />
              Komentiranje, reakcije i sudjelovanje u anketama
            </li>
            <li className="flex items-start gap-2">
              <Sparkles size={14} className="text-amber-400 mt-0.5 shrink-0" />
              Podijelite što vas zanima — newsletter prilagođen vama
            </li>
            <li className="flex items-start gap-2">
              <ShieldCheck size={14} className="text-amber-400 mt-0.5 shrink-0" />
              Privatnost zaštićena u skladu s GDPR
            </li>
          </ul>

          {/* Interests onboarding preview ("Što te najviše zanima?") — saved
              to localStorage, then auto-pushed to /api/public/me/interests
              after login (see InterestsOnboardingModal). */}
          <div className="mt-6 pt-5 border-t border-slate-800" data-testid="prijava-interests-section">
            <p className="text-center text-[12px] font-bold text-amber-300 mb-1 inline-flex items-center gap-1.5 justify-center w-full">
              <Sparkles size={14} className="text-amber-400" />
              Što te najviše zanima?
            </p>
            <p className="text-center text-[10px] text-slate-500 mb-3">
              Odaberi teme — prilagodit ćemo ti newsletter i homepage feed.
            </p>
            <div className="flex flex-wrap justify-center gap-1.5">
              {PRIJAVA_INTERESTS.map(i => {
                const on = picked.has(i.key);
                return (
                  <button key={i.key} type="button"
                          data-testid={`prijava-interest-${i.key}`}
                          onClick={() => toggleInterest(i.key)}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold border transition-colors ${
                            on
                              ? 'bg-red-600 border-red-500 text-white'
                              : 'border-slate-700 text-slate-300 hover:border-red-500 hover:text-red-300'
                          }`}>
                    {on && <Check size={10} />}
                    {i.label}
                  </button>
                );
              })}
            </div>
            {picked.size > 0 && (
              <p className="text-center text-[10px] text-emerald-400 mt-2" data-testid="prijava-interests-saved">
                ✓ Spremljeno {picked.size} interes(a) — primijenit će se nakon prijave.
              </p>
            )}
          </div>

          <p className="text-center text-[10px] text-slate-500 mt-6 leading-relaxed">
            Nastavkom prihvaćate našu{' '}
            <Link to="/politika-privatnosti" className="underline hover:text-slate-300">politiku privatnosti</Link>{' '}i{' '}
            <Link to="/pravila-komentiranja" className="underline hover:text-slate-300">pravila</Link>.
          </p>
        </div>

        <p className="text-center text-[11px] text-slate-500 mt-5">
          Administrator?{' '}
          <Link to="/admin/login" className="underline hover:text-slate-300" data-testid="prijava-admin-link">
            Prijava u admin panel
          </Link>
        </p>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c11 0 19.7-8 19.7-20 0-1.3-.1-2.3-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.3 35 26.8 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.6l6.3 5.3c-.4.4 6.7-4.9 6.7-15 0-1.3-.1-2.3-.4-3.5z"/>
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  );
}
